import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { FaArrowLeft, FaUserShield, FaChartBar, FaFileInvoice } from 'react-icons/fa';
import styles from './AdminLayout.module.css';

import { useEffect } from 'react';

const AdminLayout = () => {
    const { currentUser, isAdmin, userData } = useAuth();
    const navigate = useNavigate();

    // Allow access if superadmin, or any user with at least one dashboardAccess permission
    const canUserMgmt = userData?.role === 'superadmin' || userData?.dashboardAccess?.userManagement;
    const canDashboard = userData?.role === 'superadmin' || userData?.dashboardAccess?.dashboard;
    const canInvoices = userData?.role === 'superadmin' || userData?.dashboardAccess?.invoices;

    // Allow access if superadmin, or any user with at least one dashboardAccess permission
    const isPanelAllowed = userData?.role === 'superadmin' || (
        userData?.dashboardAccess?.userManagement ||
        userData?.dashboardAccess?.dashboard ||
        userData?.dashboardAccess?.invoices
    );

    // On mount, redirect to first available tab if not already on one
    useEffect(() => {
        if (!isPanelAllowed) return;
        const path = window.location.pathname;
        if (path === '/admin') {
            if (canUserMgmt) {
                navigate('/admin/user-management', { replace: true });
            } else if (canDashboard) {
                navigate('/admin/dashboard', { replace: true });
            } else if (canInvoices) {
                navigate('/admin/invoices', { replace: true });
            }
        }
    }, [isPanelAllowed, canUserMgmt, canDashboard, canInvoices, navigate]);

    if (!isPanelAllowed) {
        return <p className={styles.accessDenied}>🚨 Access Denied. You do not have permission to view the Admin Panel.</p>;
    }

    return (
        <div className={styles.adminLayoutContainer}>
            {/* Admin Header */}
            <div className={styles.adminHeader}>
                <button 
                    className={styles.backButton} 
                    onClick={() => navigate('/')}
                    title="Go back to home"
                >
                    <FaArrowLeft /> Back
                </button>
                <div className={styles.headerContent}>
                    <h1 className={styles.title}>Admin Panel 👑</h1>
                    <p className={styles.subtitle}>Welcome, {currentUser.email}</p>
                </div>
            </div>

            {/* Admin Navigation */}
            <nav className={styles.adminNav}>
                <ul className={styles.navList}>
                    {canUserMgmt && (
                        <li>
                            <NavLink 
                                to="/admin/user-management" 
                                className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ''}`}
                            >
                                <FaUserShield style={{marginRight: 8}}/> User Management
                            </NavLink>
                        </li>
                    )}
                    {canDashboard && (
                        <li>
                            <NavLink 
                                to="/admin/dashboard" 
                                className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ''}`}
                            >
                                <FaChartBar style={{marginRight: 8}}/> Dashboard
                            </NavLink>
                        </li>
                    )}
                    {canInvoices && (
                        <li>
                            <NavLink 
                                to="/admin/invoices" 
                                className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ''}`}
                            >
                                <FaFileInvoice style={{marginRight: 8}}/> Sales & Invoices
                            </NavLink>
                        </li>
                    )}
                </ul>
            </nav>

            {/* Admin Content */}
            <div className={styles.adminContent}>
                <Outlet />
            </div>
        </div>
    );
};

export default AdminLayout;
